import Link from 'next/link';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import styles from './receptionist.module.css';
import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';

import {
  ExternalGlyph,
  formatMoney,
  type PlanPrice,
  TWILIO_PER_MINUTE,
  TWILIO_PRICING_URL,
  TWILIO_US,
  useFormatLocale,
  usePlanPrice,
  useReceptionistT,
  useYearlyPlanPrice,
} from './kit';

const CALLS = { min: 20, max: 2000, step: 10, start: 300 };
const MINUTES = { min: 1, max: 10, step: 1, start: 3 };

/** The two bills an AI receptionist comes with, and an estimate of one. */
export default function ReceptionistCost({ price }: { price: PlanPrice }) {
  const { t } = useReceptionistT();
  const locale = useFormatLocale();
  const plan = usePlanPrice(price);
  const yearly = useYearlyPlanPrice(price);
  const usd = (amount: number, digits?: number) =>
    formatMoney(amount, 'usd', locale, digits);

  const planText = plan
    ? yearly
      ? t(
          'Build and test your agent free. Answering phone calls needs a paid plan: Starter is {{price}} a month, or {{yearly}} a month billed yearly.',
          { price: plan, yearly },
        )
      : t(
          'Build and test your agent free. Answering phone calls needs a paid plan: Starter is {{price}} a month.',
          { price: plan },
        )
    : t(
        'Build and test your agent free. Answering phone calls needs a paid plan.',
      );

  return (
    <section id='cost' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('What it costs.')}
          tail={t('Your plan, plus Twilio’s minutes.')}
          lead={t(
            'No setup fee from us. You pay for a plan here, and for the number and the call minutes at Twilio, in your own account.',
          )}
        />

        <div className='mt-14 grid gap-5 lg:grid-cols-12'>
          <div className='grid gap-5 lg:col-span-5'>
            <BillCard
              title={t('Your Easy-Peasy.AI plan')}
              text={planText}
              points={[
                t(
                  'The same agent also answers on your website, WhatsApp and more',
                ),
                t('Replies count toward your plan, the same as chat replies'),
              ]}
            >
              <Link
                href='/chatbots#pricing'
                className='group inline-flex items-center gap-2 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
              >
                {t('Compare plans')}
                <ArrowGlyph className='transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
              </Link>
            </BillCard>
            <BillCard
              title={t('Twilio, billed by Twilio')}
              text={t(
                'Your number and your call minutes are charged to your own Twilio account. Prices below are for the US.',
              )}
              points={[
                t('Local number: {{price}} a month', {
                  price: usd(TWILIO_US.localNumberPerMonth, 2),
                }),
                t('Calls: about {{price}} a minute', {
                  price: usd(TWILIO_PER_MINUTE, 2),
                }),
              ]}
            >
              <a
                href={TWILIO_PRICING_URL}
                target='_blank'
                rel='noopener noreferrer'
                className='group inline-flex items-center gap-2 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
              >
                {t('Twilio’s voice prices')}
                <ExternalGlyph className='h-4 w-4' />
              </a>
            </BillCard>
          </div>

          <div className='min-w-0 lg:col-span-7'>
            <Stage
              variant='ink'
              halftoneAt='100% 0%'
              halftoneReach={45}
              className='h-full px-6 py-8 sm:px-10 sm:py-10'
            >
              <Estimator plan={plan} />
            </Stage>
          </div>
        </div>
      </div>
    </section>
  );
}

function BillCard({
  title,
  text,
  points,
  children,
}: {
  title: string;
  text: string;
  points: string[];
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm(
        'flex flex-col rounded-[26px] p-6 sm:p-8',
        tone.surface,
        tone.line,
      )}
    >
      <h3 className={clsxm('text-lg font-semibold', tone.ink)}>{title}</h3>
      <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>{text}</p>
      <ul className='mt-5 space-y-2.5'>
        {points.map((point) => (
          <li
            key={point}
            className={clsxm('flex gap-2.5 text-[15px] leading-6', tone.ink)}
          >
            <CheckGlyph className='mt-1 h-4 w-4 text-primary-600 dark:text-primary-400' />
            {point}
          </li>
        ))}
      </ul>
      <div className='mt-6'>{children}</div>
    </div>
  );
}

function Estimator({ plan }: { plan: string | null }) {
  const { t, lang } = useReceptionistT();
  const locale = useFormatLocale();
  const id = useId();
  const [calls, setCalls] = useState(CALLS.start);
  const [length, setLength] = useState(MINUTES.start);

  const usd = (amount: number, digits?: number) =>
    formatMoney(amount, 'usd', locale, digits);
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const minutes = calls * length;
  const callCost = minutes * TWILIO_PER_MINUTE;
  const total = callCost + TWILIO_US.localNumberPerMonth;
  const checkedOn = new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${TWILIO_US.checkedOn}T00:00:00Z`));
  const direction = lang === 'ar' ? 'to left' : 'to right';
  const fill = (value: number, range: { min: number; max: number }) =>
    `linear-gradient(${direction}, rgb(var(--tw-color-primary-400)) ${
      ((value - range.min) / (range.max - range.min)) * 100
    }%, rgba(255,255,255,0.14) ${
      ((value - range.min) / (range.max - range.min)) * 100
    }%)`;

  return (
    <div className='relative flex h-full flex-col text-white'>
      <h3 className='text-lg font-semibold'>
        {t('Estimate your Twilio bill')}
      </h3>
      <p className='mt-1 text-sm text-[#a9a9c6]'>
        {t('Move the sliders to match your business.')}
      </p>

      <div className='mt-8 space-y-7'>
        <div>
          <div className='flex items-baseline justify-between gap-4'>
            <label
              htmlFor={`${id}-calls`}
              className='text-sm font-medium text-[#c9c9e0]'
            >
              {t('Calls a month')}
            </label>
            <span className='text-lg font-semibold tabular-nums'>
              {number(calls)}
            </span>
          </div>
          <input
            id={`${id}-calls`}
            type='range'
            min={CALLS.min}
            max={CALLS.max}
            step={CALLS.step}
            value={calls}
            onChange={(event) => setCalls(Number(event.target.value))}
            className={clsxm(styles.range, 'mt-3')}
            style={{ background: fill(calls, CALLS) }}
          />
        </div>
        <div>
          <div className='flex items-baseline justify-between gap-4'>
            <label
              htmlFor={`${id}-length`}
              className='text-sm font-medium text-[#c9c9e0]'
            >
              {t('Average call length')}
            </label>
            <span className='text-lg font-semibold tabular-nums'>
              {t('{{count}} min', { count: number(length) })}
            </span>
          </div>
          <input
            id={`${id}-length`}
            type='range'
            min={MINUTES.min}
            max={MINUTES.max}
            step={MINUTES.step}
            value={length}
            onChange={(event) => setLength(Number(event.target.value))}
            className={clsxm(styles.range, 'mt-3')}
            style={{ background: fill(length, MINUTES) }}
          />
        </div>
      </div>

      <div className='mt-9 rounded-[20px] bg-white/[0.06] p-5 ring-1 ring-inset ring-white/10 sm:p-6'>
        <p className='text-[13px] font-medium text-[#b7b7d2]'>
          {t('About this much a month at Twilio')}
        </p>
        <p
          aria-live='polite'
          className={clsxm(
            agents.display,
            'mt-1 text-4xl font-semibold tabular-nums sm:text-5xl',
          )}
        >
          {usd(total, 2)}
        </p>
        <dl className='mt-5 space-y-2 border-t border-white/10 pt-4 text-sm'>
          <div className='flex justify-between gap-4'>
            <dt className='text-[#a9a9c6]'>
              {t('{{minutes}} minutes of calls', { minutes: number(minutes) })}
            </dt>
            <dd className='font-medium tabular-nums'>{usd(callCost, 2)}</dd>
          </div>
          <div className='flex justify-between gap-4'>
            <dt className='text-[#a9a9c6]'>{t('Local phone number')}</dt>
            <dd className='font-medium tabular-nums'>
              {usd(TWILIO_US.localNumberPerMonth, 2)}
            </dd>
          </div>
        </dl>
      </div>

      <p className='mt-5 text-sm leading-6 text-[#c9c9e0]'>
        {plan
          ? t('Plus your Easy-Peasy.AI plan, from {{price}} a month.', {
              price: plan,
            })
          : t('Plus your Easy-Peasy.AI plan.')}
      </p>
      <p className='mt-auto pt-6 text-xs leading-5 text-[#8a8aa8]'>
        {t(
          'Twilio’s US prices on {{date}}: {{relay}} a minute for ConversationRelay, which listens and speaks on the call, {{receive}} a minute to receive a call on a local number, and {{number}} a month for the number. Prices differ by country and can change.',
          {
            date: checkedOn,
            relay: usd(TWILIO_US.relayPerMinute, 2),
            receive: usd(TWILIO_US.receivePerMinute, 4),
            number: usd(TWILIO_US.localNumberPerMonth, 2),
          },
        )}
      </p>
    </div>
  );
}
